/**
 * Start a dedicated agent-panel chat for Basecamp todo notes updates.
 * Avoids injecting drafts into unrelated open conversations.
 */

import { dispatchScopedChatAutoSend } from '@/lib/open-inbox-thread-in-chat'
import { noteFieldDef } from '@/lib/note-field-catalog'
import type { ViewContext } from '@/components/layout/chat-pane.types'

/** First-turn prompt when generating notes from the edit pane (todo is already context). */
export const UPDATE_TODO_NOTES_PROMPT = 'Update the todo notes for this Basecamp todo.'

const AUTO_SEND_MS = 350
const DRAFT_DESCRIPTION_CAP = 4000

export type BasecampTodoNotesThreadDetail = {
  todoId: string
  threadId: string
}

export type TodoNotesFormField = {
  key: string
  label: string
  value: string
}

export type BeginBasecampTodoNotesChatOptions = {
  todoId: string
  todoTitle: string
  projectId?: string
  /** Document tab id for context chip (defaults to basecamp-todo-{todoId}). */
  contextDocumentId?: string
  /** In-progress Edit form values (unsaved). Sent as viewContext, not the user bubble. */
  formFields?: TodoNotesFormField[]
  draftDescription?: string
}

export function snapshotTodoNotesForm(input: {
  frontmatter?: Record<string, string>
  descriptionText?: string
  dueOn?: string
  assignees?: Array<{ name?: string | null } | null>
}): { formFields: TodoNotesFormField[]; draftDescription: string } {
  const formFields: TodoNotesFormField[] = []
  const dueOn = input.dueOn?.trim()
  if (dueOn) formFields.push({ key: 'due_on', label: 'Due on', value: dueOn })
  const assigneeNames = (input.assignees || [])
    .map((person) => person?.name?.trim())
    .filter((name): name is string => Boolean(name))
  if (assigneeNames.length) {
    formFields.push({ key: 'assignees', label: 'Assignees', value: assigneeNames.join(', ') })
  }
  for (const [key, raw] of Object.entries(input.frontmatter || {})) {
    const value = String(raw || '').trim()
    if (!value) continue
    formFields.push({
      key,
      label: noteFieldDef(key)?.label || key.replace(/_/g, ' '),
      value,
    })
  }
  return {
    formFields,
    draftDescription: String(input.descriptionText || '').trim().slice(0, DRAFT_DESCRIPTION_CAP),
  }
}

function viewContextFromNotesChat(options: BeginBasecampTodoNotesChatOptions): ViewContext {
  return {
    type: 'basecamp-todo',
    todoId: options.todoId,
    projectId: options.projectId || '',
    title: options.todoTitle,
    ...(options.formFields?.length ? { formFields: options.formFields } : {}),
    ...(options.draftDescription?.trim()
      ? { draftDescription: options.draftDescription.trim().slice(0, DRAFT_DESCRIPTION_CAP) }
      : {}),
  }
}

function truncateTitle(title: string, maxLen = 56): string {
  const trimmed = title.trim()
  if (trimmed.length <= maxLen) return trimmed
  return `${trimmed.slice(0, maxLen - 3).trim()}...`
}

/** Create a fresh agent-panel chat and return the thread id for event routing. */
export function beginBasecampTodoNotesChat(
  options: BeginBasecampTodoNotesChatOptions,
): string {
  const threadId = `new-${Date.now()}`
  const contextDocumentId = options.contextDocumentId ?? `basecamp-todo-${options.todoId}`

  window.dispatchEvent(
    new CustomEvent('chat-create-new', {
      detail: {
        threadId,
        forceAgentPanel: true,
        preferNew: true,
        contextDocumentId,
        contextDocumentTitle: options.todoTitle,
        threadTitle: truncateTitle(`Update notes: ${options.todoTitle}`),
      },
    }),
  )

  return threadId
}

/** New agent chat with the todo as context, then send the update-notes instruction. */
export function beginBasecampTodoNotesAgentChat(
  options: BeginBasecampTodoNotesChatOptions,
): string {
  const threadId = beginBasecampTodoNotesChat(options)
  const viewContext = viewContextFromNotesChat(options)
  window.setTimeout(() => {
    dispatchScopedChatAutoSend(UPDATE_TODO_NOTES_PROMPT, threadId, { viewContext })
  }, AUTO_SEND_MS)
  return threadId
}

export function isBasecampTodoNotesEventForThread(
  eventThreadId: string | undefined,
  hostedThreadId: string | undefined,
): boolean {
  if (!eventThreadId || !hostedThreadId) return false
  return eventThreadId === hostedThreadId
}
